import React, { createContext, useEffect, useState, useRef, useCallback } from 'react';
import Keycloak from 'keycloak-js';
import useStickyState, { StickyStateKeys } from '../hooks/utils/useStickyState';
import { analytics, EventTypes } from '../utils/analytics';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { axiosPost } from '../api/axios-handler';
import { BootSplash, isWelcomePage, markSkipBootSplash } from '../components/common/boot-splash';
import { Authenticate, User, userQueryKeys, useUser } from '../hooks/api/useUser';
import { AxiosResponse } from 'axios';
import Toast from '../components/common/pop-up-message';
import { useHubspotEventMutation } from '../hooks/api/useEvent';
import { setSessionStorageItem, useSessionStorage } from '../hooks/utils/useSessionStorage';
import { getMsUntilExpiration, getTokenExpiration } from '../utils/jwt';
import { resolveMwaKeycloakClientId } from '../utils/mwa-keycloak-client-id';
import { stripStaleOAuthParams } from '../utils/stale-oauth-params';

/** Signals a fresh login (vs. token refresh or reload) so Fi and Peersona can start clean. */
const markFreshAuthSession = (): void => {
  setSessionStorageItem('kh:fi:freshSession', '1');
  setSessionStorageItem('kh:peersona:freshSession', '1');
};

/** Peersona-only fresh signal — used when a new tab silently inherits a token. */
const markPeersonaFreshSession = (): void => {
  setSessionStorageItem('kh:peersona:freshSession', '1');
};

/** Set on explicit logout; next authenticated session treats as fresh login even
 * without a Keycloak OAuth callback (``?code=&state=``) in the URL. */
const EXPECT_FRESH_ASSISTANT_KEY = 'kh:expectFreshAssistant';

interface AuthContextProps {
  keycloak: Keycloak | null;
  authToken: string | null;
  setAuthToken: React.Dispatch<string | null>;
  logout: () => void;
  user: User | null;
  isLoading: boolean;
  redirectTo: string | null;
  setRedirectTo: React.Dispatch<string | null>;
  authenticate: UseMutationResult<
    AxiosResponse<User, any>,
    unknown,
    Authenticate & {
      setDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    },
    unknown
  >;
  authenticateWithToken: UseMutationResult<
    any,
    unknown,
    {
      token: string;
      userId: number;
    },
    unknown
  >;
  authenticateWithSSOToken: UseMutationResult<
    User,
    unknown,
    {
      token: string;
    },
    unknown
  >;
  getNewToken: (onExpired?: boolean) => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const keycloakConfig = {
  url: process.env.REACT_APP_KEYCLOAK_IDP_URL!,
  realm: process.env.REACT_APP_KEYCLOAK_IDP_REALM!,
  clientId: resolveMwaKeycloakClientId(),
  baseUrl: process.env.REACT_APP_BASE_URL!,
};

const TOKEN_EXPIRATION_THRESHOLD = 60; // seconds
const MIN_CHECK_INTERVAL = 30000; // milliseconds (30 seconds - minimum check interval)
const MAX_CHECK_INTERVAL = 240000; // milliseconds (4 minutes - maximum check interval, aligns with 5-minute token lifetime)

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const isFirstRun = useRef<boolean>(false);
  const keycloak = useRef<Keycloak | null>(null);
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useStickyState(StickyStateKeys.AuthorizationToken, null);
  const prevTokenExpirationRef = useRef<number | null>(null);
  const { data: user, isFetched: tempUserIsLoading } = useUser(Boolean(authToken));
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const hubspotMutation = useHubspotEventMutation();
  const loggedIn = useSessionStorage('loggedIn');
  if (!loggedIn) {
    setSessionStorageItem('loggedIn', 'false');
  }

  const isLoading = (authToken && !tempUserIsLoading) || !keycloak.current;

  const authenticate = useMutation(
    ({
      email_address,
      password,
      trusted,
    }: Authenticate & { setDisabled: React.Dispatch<React.SetStateAction<boolean>> }) => {
      return axiosPost(`/users/authenticate`, {
        email_address,
        password,
        trusted,
      }).then((response) => {
        analytics.trackEvent(EventTypes.LOGIN, 'WebApp');
        return response;
      });
    },
    {
      onSuccess: (res: AxiosResponse<User>, { setDisabled }) => {
        markFreshAuthSession();
        setAuthToken(res.data.authorization_token ?? null);
        queryClient.invalidateQueries(userQueryKeys.me);
      },
      onError: () => {
        Toast.error('Email and/or password is not correct.');
      },
      onSettled: (data, error, { setDisabled }) => {
        setDisabled(false);
      },
    },
  );

  const authenticateWithToken = useMutation(
    ({ token, userId }: { token: string; userId: number }) => {
      const data = {
        token,
      };
      return axiosPost(`/users/${userId}/authenticate_with_token`, data, 'v3');
    },
    {
      onSuccess: (result: any) => {
        markFreshAuthSession();
        setAuthToken(result.data.authorization_token ?? null);
        analytics.trackEvent(EventTypes.LOGIN, 'SmartLink');
        queryClient.invalidateQueries(userQueryKeys.me);
      },
    },
  );

  const authenticateWithSSOToken = useMutation(
    ({ token }: { token: string }) => {
      return axiosPost('/users/authenticate_with_sso_token', { token }, 'v3').then((response) => response.data);
    },
    {
      onSuccess: (result: User) => {
        markFreshAuthSession();
        setAuthToken(result?.authorization_token ?? null);
        analytics.trackEvent(EventTypes.LOGIN, 'SSO');
        queryClient.invalidateQueries(userQueryKeys.me);
      },
    },
  );

  const logout = async () => {
    markSkipBootSplash();
    setAuthToken(null);
    setSessionStorageItem('loggedIn', 'false');
    setSessionStorageItem(EXPECT_FRESH_ASSISTANT_KEY, '1');
    sessionStorage.removeItem('pathwaysEnabled');
    sessionStorage.removeItem('topic_collections_order');
    queryClient.removeQueries(['me']);
    queryClient.removeQueries(['fi']);
    axiosPost(`/users/deauthenticate`, {});
    if (keycloak) {
      keycloak.current?.logout();
    }
    analytics.trackEvent(EventTypes.LOGOUT);
    localStorage.clear();
  };

  const initKeycloak = async () => {
    /* Progressive timeout strategy for slow network connections (3G, etc.):
     *
     * On slow connections, Keycloak's iframe communication can fail, causing network requests
     * to be cancelled and redirect loops. Safari is particularly problematic with iframe
     * communication, but this affects all browsers on slow connections.
     *
     * Testing showed that fixed timeouts consistently fail on slow connections,
     * but the progressive approach (2s, 4s, 6s, 8s, etc.) succeeds on subsequent attempts.
     *
     * The reason for this success is unclear - it could be related to:
     * - Network timing and browser state cycles
     * - Keycloak server state changes between attempts
     * - Browser iframe communication timing
     * - Some other unknown factor
     *
     * Regardless of the root cause, this progressive approach empirically worked better
     * in testing than fixed timeouts for slow connection scenarios.
     */
    let timer: NodeJS.Timeout | null = null;

    const currentReloads = parseInt(sessionStorage.getItem('keycloakReloads') || '0');
    const timeoutDuration = (currentReloads + 1) * 2000; // 2s, 4s, 6s, 8s, etc.

    const setupFallbackTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        console.log(
          `Keycloak initialization timeout (attempt ${currentReloads + 1}) - forcing reload for Safari compatibility`,
        );
        sessionStorage.setItem('keycloakReloads', (currentReloads + 1).toString());
        window.location.reload();
      }, timeoutDuration);
    };

    const keycloakInstance: Keycloak = new Keycloak(keycloakConfig);

    keycloakInstance.onAuthSuccess = () => {
      setAuthToken(keycloakInstance.token ?? null);
    };

    keycloakInstance.onTokenExpired = () => {
      keycloakInstance
        .updateToken()
        .then((refreshed) => {
          if (refreshed) {
            queryClient.invalidateQueries(userQueryKeys.me);
            setAuthToken(keycloakInstance.token ?? null);
          }
        })
        .catch(() => {
          setAuthToken(null);
          setSessionStorageItem('loggedIn', 'false');
        });
    };

    keycloakInstance.onAuthLogout = () => {
      setAuthToken(null);
      setSessionStorageItem('loggedIn', 'false');
    };

    // Logged-out welcome paints immediately. check-sso opens Keycloak iframes and,
    // if they stall, reloads the page. A stored token still needs check-sso: `/`
    // redirects into the app, and without it this instance never refreshes.
    const hasStoredSession = Boolean(localStorage.getItem(StickyStateKeys.AuthorizationToken));
    const skipSessionCheck = isWelcomePage(window.location.pathname) && !hasStoredSession;
    if (!skipSessionCheck) {
      setupFallbackTimer();
    }

    const cleanedPath = stripStaleOAuthParams(window.location.href);
    if (cleanedPath !== null) {
      window.history.replaceState(null, '', cleanedPath);
    }

    keycloakInstance
      .init({
        // Logged-out welcome stays logged out until Log In. A stored session, and
        // every other page, still restores through check-sso.
        // checkLoginIframe would still open the third-party cookie iframe before onLoad runs.
        ...(skipSessionCheck ? { checkLoginIframe: false } : { onLoad: 'check-sso' as const }),
        // Magic links and standard OIDC code flow redirect with ?code=&state= on the path.
        // Default 'fragment' mode never parses those callbacks, so auth silently fails.
        responseMode: 'query',
      })
      .then((authenticated: boolean) => {
        sessionStorage.removeItem('keycloakReloads');

        const hash = window.location.hash;
        const search = window.location.search;
        const hasKeycloakParams =
          authenticated &&
          ((hash.includes('state=') && hash.includes('code=')) ||
            (search.includes('state=') && search.includes('code=')));

        // Clean up Keycloak redirect parameters from URL after successful auth
        if (hasKeycloakParams) {
          if (hash.includes('state=') && hash.includes('code=')) {
            const routeMatch = hash.match(/#(\/[^?#]*)/);
            const cleanHash = routeMatch ? routeMatch[1] : '';
            window.history.replaceState(null, '', cleanHash || window.location.pathname + window.location.search);
          }
          if (search.includes('state=') && search.includes('code=')) {
            const url = new URL(window.location.href);
            url.searchParams.delete('state');
            url.searchParams.delete('code');
            url.searchParams.delete('session_state');
            url.searchParams.delete('iss');
            window.history.replaceState(null, '', url.pathname + (url.search || '') + (url.hash || ''));
          }
        }

        if (authenticated && keycloakInstance.token) {
          const expectFreshAssistant = sessionStorage.getItem(EXPECT_FRESH_ASSISTANT_KEY) === '1';
          if (hasKeycloakParams || expectFreshAssistant) {
            markFreshAuthSession();
            if (expectFreshAssistant) {
              sessionStorage.removeItem(EXPECT_FRESH_ASSISTANT_KEY);
            }
          } else if (loggedIn === 'false') {
            // New tab inheriting a shared token — fresh Peersona only so we
            // don't reset an active Fi thread in another tab.
            markPeersonaFreshSession();
          }
          setAuthToken(keycloakInstance.token);
          if (loggedIn === 'false') {
            hubspotMutation.mutate({
              event: 'login',
            });
            setSessionStorageItem('loggedIn', 'true');
          }
        }
      })
      .catch((error) => {
        sessionStorage.removeItem('keycloakReloads');
        console.error('Keycloak initialization failed:', error);
        setAuthToken(null);
      })
      .finally(() => {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        keycloak.current = keycloakInstance;
        setLoading(false);
      });
  };

  // Function to check and refresh the token
  const getNewToken = (onExpired = false) => {
    if (keycloak.current?.authenticated) {
      if (onExpired && !keycloak.current.isTokenExpired(TOKEN_EXPIRATION_THRESHOLD)) {
        return;
      }

      keycloak.current
        // we already know that the token is expired, so we can force a refresh
        .updateToken(0)
        .then((refreshed) => {
          if (refreshed) {
            setAuthToken(keycloak.current?.token ?? null);
          }
        })
        .catch(() => {
          setAuthToken(null);
        });
    }
  };
  const initKeycloakCallback = useCallback(initKeycloak, [hubspotMutation, loggedIn, queryClient, setAuthToken]);
  const getNewTokenCallback = useCallback(getNewToken, [setAuthToken]);

  // Initialize Keycloak on first run
  useEffect(() => {
    if (isFirstRun.current) return;
    isFirstRun.current = true;
    initKeycloakCallback();
  }, [initKeycloakCallback]);

  // This useEffect is used to check the token expiration time and only refresh the token if it's close to expiring
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    let intervalId: NodeJS.Timeout | null = null;

    // Only reschedule if token expiration changed significantly (more than 10 seconds difference)
    let currentExpiration: number | null = null;
    if (authToken) {
      const exp = getTokenExpiration(authToken);
      if (exp) {
        currentExpiration = exp;
      }
    }
    const expirationChanged =
      prevTokenExpirationRef.current === null ||
      currentExpiration === null ||
      Math.abs((prevTokenExpirationRef.current - (currentExpiration || 0)) * 1000) > 10000; // 10 seconds difference

    if (!expirationChanged && prevTokenExpirationRef.current !== null) {
      return;
    }
    prevTokenExpirationRef.current = currentExpiration;

    const scheduleNextCheck = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);

      if (!authToken) {
        // if the token is not set, set an interval to check every 30 seconds
        intervalId = setInterval(() => getNewTokenCallback(true), MIN_CHECK_INTERVAL);
        return;
      }

      const msUntilExpiration = getMsUntilExpiration(authToken);
      if (msUntilExpiration === null) {
        // if the expiration time is not set, set an interval to check every 30 seconds
        intervalId = setInterval(() => getNewTokenCallback(true), MIN_CHECK_INTERVAL);
        return;
      }

      const msUntilCheck = msUntilExpiration - TOKEN_EXPIRATION_THRESHOLD * 1000; // 60 seconds before expiration

      if (msUntilCheck <= 0) {
        // Token expires soon, refresh immediately - the new token will have a new expiration
        // and the useEffect will re-run to reschedule based on the new token
        getNewTokenCallback(true);
        return;
      }

      // Schedule refresh for when token is 60 seconds before expiration
      // When refreshed, the new token will have a new expiration time, and the useEffect
      // will re-run to reschedule based on the new token's expiration
      const checkInterval = Math.min(msUntilCheck, MAX_CHECK_INTERVAL);
      timeoutId = setTimeout(() => {
        getNewTokenCallback(true);
        scheduleNextCheck();
      }, checkInterval);
    };

    scheduleNextCheck();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [authToken, getNewTokenCallback]);

  const bootDataReady = !loading && !(isLoading && !user);
  const authenticated = Boolean(authToken || user);

  return (
    <AuthContext.Provider
      value={{
        keycloak: keycloak.current,
        authToken,
        setAuthToken,
        logout,
        user: (user as User) || null,
        isLoading,
        authenticate,
        authenticateWithToken,
        authenticateWithSSOToken,
        getNewToken,
        redirectTo,
        setRedirectTo,
      }}
    >
      <BootSplash bootDataReady={bootDataReady} authenticated={authenticated}>
        {children}
      </BootSplash>
    </AuthContext.Provider>
  );
};

export { AuthProvider, AuthContext };
